import { Signal } from '@verlo/shared/components/ui/signal';
import { Notice } from '@verlo/shared/components/ui/notice';
import { BRAND } from '@verlo/shared/lib/brand';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { serviceApi } from '@verlo/shared/services/api';
import { useAuth } from '@verlo/shared/contexts/AuthContext';
import {
  ArrowDownRight, ArrowUpRight, Clock, RefreshCw,
} from 'lucide-react';

// Verlo Service — the manager's live floor view (auth plan item 6 / 6b).
// One screen: exceptions first, then net sales vs budget and vs the equivalent day,
// the pace curve, covers / spend / projection, labour (6b part 2: clocked hours at gross
// wage, rota hours where nobody clocked — who's in, who's due, what it's costing), and the
// void-discount-comp + manager activity strips. Polls /service/live; every figure carries
// "as of".

const POLL_MS = 20000;

const gbp = (n) =>
  n == null ? '—' : `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const gbp0 = (n) =>
  n == null ? '—' : `£${Math.round(Number(n)).toLocaleString('en-GB')}`;
const pct = (n) => (n == null ? '—' : `${n > 0 ? '+' : ''}${n.toFixed(0)}%`);
const clock = (iso) => {
  try { return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

// A shift's state on the labour card. `due` is the one a manager acts on: the rota says
// they're working and nobody has clocked in — costed from the rota meanwhile.
const SHIFT_STATE = {
  clocked_in: { label: 'In', tone: 'ok' },
  due: { label: 'Due · not clocked in', tone: 'warn' },
  completed: { label: 'Done', tone: null },
  unclocked: { label: 'Not clocked', tone: null },
  scheduled: { label: 'Later', tone: null },
};

// The one word a manager reads an exception by. The detail says the rest.
const EXCEPTION_WORD = {
  big_void: 'Void', long_table: 'Table', adjustments_hot: 'Comps', not_clocked_in: 'Rota', labour_hot: 'Labour',
  offer_hot: 'Offer', offer_no_trigger: 'Offer',
};
const hrs = (n) => (n == null ? '—' : `${Number(n).toFixed(1)}h`);

const EVENT_LABEL = {
  order_refunded: 'Refund', order_cancelled: 'Void', item_voided: 'Item void',
  discount_applied: 'Discount', discount_removed: 'Discount off', item_comped: 'Comp',
  item_uncomped: 'Comp off', price_overridden: 'Price override', no_sale: 'No-sale',
  promotion_applied: 'Offer', promotion_removed: 'Offer off',
};
const SIGNAL_TONE = { ok: 'ok', warn: 'warn', stop: 'stop', info: 'info' };

// Two cumulative curves (today, and the equivalent day) as a single inline SVG.
const Sparkline = ({ today = [], equivalent = [] }) => {
  const W = 640;
  const H = 120;
  const all = [...today, ...equivalent];
  if (all.length < 2) return <div className="h-[120px] flex items-center text-sm text-ink-faint">Not enough of the day yet.</div>;
  const maxX = Math.max(...all.map((p) => p.minute_offset), 1);
  const maxY = Math.max(...all.map((p) => p.cumulative), 1);
  const path = (pts) => pts
    .map((p, i) => `${i ? 'L' : 'M'} ${(p.minute_offset / maxX) * W} ${H - (p.cumulative / maxY) * H}`)
    .join(' ');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[120px]" preserveAspectRatio="none">
      <path d={path(equivalent)} fill="none" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="4 4" />
      <path d={path(today)} fill="none" stroke={BRAND.verlo} strokeWidth="3" />
    </svg>
  );
};

const Delta = ({ pounds, pct: p }) => {
  const up = (pounds ?? 0) >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${up ? 'text-signal-ok' : 'text-signal-stop'}`}>
      <Icon size={20} />
      {gbp(Math.abs(pounds ?? 0))}
      {p != null && <span className="text-ink-faint font-normal">({pct(p)})</span>}
    </span>
  );
};

const ServicePage = () => {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [stale, setStale] = useState(false);
  const timer = useRef(null);

  const allowed = hasPermission('service.view');

  const load = async () => {
    try {
      const res = await serviceApi.live();
      setData(res.data);
      setErr(null);
      setStale(false);
    } catch (e) {
      if (e?.response?.status === 403) setErr('forbidden');
      else setStale(true);       // keep showing the last good numbers, flag them stale
    }
  };

  useEffect(() => {
    if (!allowed) return undefined;
    load();
    timer.current = setInterval(load, POLL_MS);
    return () => clearInterval(timer.current);
  }, [allowed]);

  const proj = data?.projection;
  const cmp = data?.comparison;
  const lab = data?.labour;
  // The server strips the £ for a caller without the permission; mirror it so the card
  // never shows an empty cost column.
  const showCost = hasPermission('service.view_labour_cost') && lab?.actual_cost != null;

  const exceptions = data?.exceptions || [];

  if (!allowed || err === 'forbidden') {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center text-center gap-3">
        <p className="text-lg font-medium text-ink-soft">Verlo Service isn’t available on your account.</p>
        <button onClick={() => navigate('/tables')} className="text-sm text-verlo hover:underline">Back to the floor</button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-4 space-y-4" data-testid="service-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-ink">Service</h1>
        <div className="flex items-center gap-2 text-xs text-ink-faint">
          <Clock size={13} />
          {data ? `as of ${clock(data.as_of)}` : 'loading…'}
          {stale && <span className="text-signal-warn font-medium">· reconnecting</span>}
          <button onClick={load} className="ml-1 text-ink-faint hover:text-ink-soft" title="Refresh now">
            <RefreshCw size={13} />
          </button>
        </div>
      </div>

      {/* Exceptions first — "is anything on fire?" */}
      {exceptions.length > 0 && (
        <div className="space-y-1.5" data-testid="service-exceptions">
          {exceptions.map((e, i) => (
            <Notice key={i} tone={e.severity === 'high' ? 'stop' : 'warn'} word={EXCEPTION_WORD[e.kind] || 'Note'}>
              {e.detail}
            </Notice>
          ))}
        </div>
      )}

      {/* Headline: net sales vs budget · vs equivalent day */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border bg-white p-4">
          <p className="text-[11px] uppercase tracking-wide text-ink-faint">Net sales today</p>
          <p className="text-4xl font-bold text-ink mt-1" data-testid="service-net-sales">
            {data ? gbp(data.net_sales?.pounds) : '—'}
          </p>
          {data && 'budget' in data && (
            <div className="mt-2 text-sm">
              {data.budget?.source === 'synced' ? (
                <span className="text-ink-soft">
                  Budget {gbp0(data.budget.pounds)} · <Delta pounds={data.vs_budget?.delta_pounds} pct={data.vs_budget?.delta_pct} />
                </span>
              ) : (
                <span className="text-ink-faint">No budget synced for today</span>
              )}
            </div>
          )}
        </div>

        <div className="rounded-xl border bg-white p-4">
          <p className="text-[11px] uppercase tracking-wide text-ink-faint">
            vs {cmp ? new Date(cmp.equivalent_day).toLocaleDateString('en-GB', { weekday: 'long' }) : 'last week'}, same time
          </p>
          <div className="text-3xl font-bold mt-1" data-testid="service-comparison">
            {cmp ? <Delta pounds={cmp.delta_pounds} pct={cmp.delta_pct} /> : '—'}
          </div>
          {cmp && (
            <p className="mt-2 text-sm text-ink-soft">
              they were at {gbp(cmp.value_at_same_point)} · finished {gbp0(cmp.full_day)}
            </p>
          )}
        </div>
      </div>

      {/* Pace */}
      <div className="rounded-xl border bg-white p-4">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[11px] uppercase tracking-wide text-ink-faint">Pace</p>
          <p className="text-[11px] text-ink-faint">
            <span className="inline-block w-3 border-t-2 border-verlo align-middle mr-1" />today
            <span className="inline-block w-3 border-t-2 border-dashed border-ink-faint align-middle ml-3 mr-1" />
            {cmp ? new Date(cmp.equivalent_day).toLocaleDateString('en-GB', { weekday: 'short' }) : 'last wk'}
          </p>
        </div>
        <Sparkline today={data?.pace} equivalent={cmp?.curve} />
      </div>

      {/* Covers · spend · projection */}
      <div className="grid grid-cols-3 gap-4 text-center">
        <div className="rounded-xl border bg-white p-3">
          <p className="text-[11px] uppercase tracking-wide text-ink-faint">Covers</p>
          <p className="text-2xl font-bold text-ink" data-testid="service-covers">{data?.covers ?? '—'}</p>
        </div>
        <div className="rounded-xl border bg-white p-3">
          <p className="text-[11px] uppercase tracking-wide text-ink-faint">Avg / head</p>
          <p className="text-2xl font-bold text-ink">{data ? gbp(data.avg_spend_per_head) : '—'}</p>
        </div>
        <div className="rounded-xl border bg-white p-3">
          <p className="text-[11px] uppercase tracking-wide text-ink-faint">Projected close</p>
          <p className="text-2xl font-bold text-ink">{proj ? gbp0(proj.net_sales_close) : '—'}</p>
          <p className="text-[10px] text-ink-faint leading-tight mt-0.5">{proj?.method}</p>
        </div>
      </div>

      {/* Labour — who's in, who's due, what it's costing */}
      <div className="rounded-xl border bg-white p-4" data-testid="service-labour">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-wide text-ink-faint">Labour</p>
          {lab?.source === 'synced' && (
            <p className="text-[11px] text-ink-faint">
              {lab.clocked_in} in · {lab.due_now} due · {lab.scheduled} rota'd ({hrs(lab.scheduled_hours)})
            </p>
          )}
        </div>
        {!lab || lab.source !== 'synced' ? (
          <p className="text-sm text-ink-faint">No rota synced for today.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {showCost && (
              <div className="md:col-span-2">
                <p className="text-3xl font-bold text-ink" data-testid="service-labour-cost">
                  {gbp(lab.actual_cost)}
                  {lab.labour_pct != null && (
                    <span className="text-base font-semibold text-ink-soft ml-2">{lab.labour_pct.toFixed(1)}%</span>
                  )}
                </p>
                <p className="text-sm text-ink-soft mt-1">
                  projected {gbp0(lab.projected_cost)}
                  {lab.projected_pct != null && ` · ${lab.projected_pct.toFixed(1)}% of projected sales`}
                </p>
                {lab.budget && (lab.budget.pct != null || lab.budget.cost != null) && (
                  <p className="text-sm text-ink-soft">
                    budget {lab.budget.pct != null ? `${lab.budget.pct.toFixed(1)}%` : gbp0(lab.budget.cost)}
                  </p>
                )}
                <p className="text-[10px] text-ink-faint leading-tight mt-1">{lab.method}</p>
              </div>
            )}
            <div className={showCost ? 'md:col-span-3' : 'md:col-span-5'}>
              <div className="space-y-1 max-h-52 overflow-y-auto">
                {lab.shifts.length === 0 && <p className="text-sm text-ink-faint">Nobody on the rota today.</p>}
                {lab.shifts.map((sh) => {
                  const st = SHIFT_STATE[sh.state] || SHIFT_STATE.scheduled;
                  return (
                    <div key={sh.shift_id} className="flex items-center gap-2 text-xs py-1 pr-2 border-b last:border-0">
                      <span className="font-medium text-ink-soft truncate min-w-0 flex-1">{sh.staff_name}</span>
                      <span className="text-ink-faint shrink-0 hidden sm:inline">{sh.role}</span>
                      {st.tone
                        ? <Signal tone={st.tone} className="shrink-0">{st.label}{sh.state === 'clocked_in' && sh.since ? ` ${clock(sh.since)}` : ''}</Signal>
                        : <span className="shrink-0 text-ink-faint">{st.label}</span>}
                      <span className="text-ink-soft tabular-nums shrink-0 w-10 text-right">{hrs(sh.worked_hours)}</span>
                      {showCost && <span className="text-ink-soft tabular-nums shrink-0 w-16 text-right">{gbp(sh.cost)}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Adjustments · manager activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-xl border bg-white p-4">
          <p className="text-[11px] uppercase tracking-wide text-ink-faint mb-2">Voids · discounts · comps</p>
          {['void', 'discount', 'comp', 'offer'].map((k) => (
            <div key={k} className="flex justify-between py-1 text-sm border-b last:border-0">
              <span className="capitalize text-ink-soft">{k}s</span>
              <span className="font-medium">
                {gbp(data?.adjustments?.[k]?.pounds)}
                <span className="text-ink-faint ml-2">{data?.adjustments?.[k]?.pct_of_net ?? 0}%</span>
              </span>
            </div>
          ))}
        </div>

        <div className="rounded-xl border bg-white p-4">
          <p className="text-[11px] uppercase tracking-wide text-ink-faint mb-2">Manager activity</p>
          <div className="space-y-1 max-h-52 overflow-y-auto">
            {(data?.activity || []).length === 0 && <p className="text-sm text-ink-faint">Nothing yet today.</p>}
            {(data?.activity || []).map((a, i) => (
              <div key={i} className="flex gap-2 text-xs">
                <span className="text-ink-faint tabular-nums shrink-0">{clock(a.at)}</span>
                <span className="font-medium text-ink-soft shrink-0">{EVENT_LABEL[a.event] || a.event}</span>
                <span className="text-ink-soft truncate">{a.detail}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Offers live (promotions plan §5): one row per offer running right now — today's
          take-up, the £ given, and the same weekday one week back at this point. Nothing
          when none is running. */}
      {(data?.offers || []).length > 0 && (
        <div className="rounded-xl border bg-white p-4" data-testid="offers-live">
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-[11px] uppercase tracking-wide text-ink-faint">Offers live</p>
            <p className="text-[11px] text-ink-faint">vs {data?.comparison?.equivalent_day ? 'last week' : 'the equivalent day'} at this time</p>
          </div>
          {data.offers.map((o) => (
            <div key={o.id} className="grid grid-cols-[1.6fr_64px_1.1fr_1fr] gap-4 items-center py-2 border-b last:border-0" data-testid={`offer-row-${o.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{o.name}</p>
                <p className="text-[12px] text-ink-faint truncate">
                  {o.words}{o.trigger_mode === 'tap' ? ' · tap' : ''}
                </p>
              </div>
              <p className="text-[22px] font-extrabold tabular-nums text-right leading-none">{o.redemptions}</p>
              <p className="text-[12.5px] text-ink-soft tabular-nums">
                <span className="font-semibold text-ink">−{gbp(o.pounds)}</span> given
                {o.funded > 0 && <span> · {gbp(o.funded)} funded</span>}
              </p>
              <div className="text-right">
                {o.signal?.tone && SIGNAL_TONE[o.signal.tone]
                  ? <Signal tone={SIGNAL_TONE[o.signal.tone]}>{o.signal.label}</Signal>
                  : <span className="text-[11px] text-ink-faint">{o.signal?.label}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ServicePage;
